import { useUser } from '@app/context/UserContext';
import { useNavigation } from '@app/hooks/useNavigation';
import { useEffect, useState } from 'react';

// Chatwoot-Pendant zu HubSpotChat: selbst gehostetes Widget von
// VITE_CHATWOOT_BASE_URL, Identity Validation über den serverseitig berechneten
// chatwootIdentifierHash (Ersatz für hubspotIdToken).
//
// Sichtbarkeit folgt derselben Logik wie beim HubSpot-Widget: Die Bubble
// erscheint erst, wenn der KI-Chat an einen Menschen übergibt
// (nav.hubspot.showChat) — die nav-Keys behalten ihren Namen, damit der
// Provider-Wechsel keinen State-Umbau erzwingt.
//
// Consent: Chatwoot läuft first-party auf eigener Infrastruktur (keine
// Drittlandübermittlung). Mit VITE_CHATWOOT_REQUIRE_CONSENT=true lädt das
// Widget trotzdem erst nach Usercentrics-Freigabe (nav.consent.hubspot).

const BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL || '';
const WEBSITE_TOKEN = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN || '';
const REQUIRE_CONSENT = import.meta.env.VITE_CHATWOOT_REQUIRE_CONSENT === 'true';
const SCRIPT_ID = 'chatwoot-sdk-script';

export function ChatwootChat() {
  const { user } = useUser();
  const {
    navigation: {
      hubspot: { showChat },
      consent,
    },
    setNavigation,
  } = useNavigation();

  const [ready, setReady] = useState(false);
  const consentOk = !REQUIRE_CONSENT || consent.hubspot;

  // Widget-Events -> Navigation-State (ersetzt HubSpots ResizeObserver-Hack)
  useEffect(() => {
    const onReady = () => {
      setReady(true);
      setNavigation((nav) => void (nav.hubspot.chatIsLoaded = true));
    };
    const onOpened = () => setNavigation((nav) => void (nav.hubspot.chatIsOpen = true));
    const onClosed = () => setNavigation((nav) => void (nav.hubspot.chatIsOpen = false));

    window.addEventListener('chatwoot:ready', onReady);
    window.addEventListener('chatwoot:opened', onOpened);
    window.addEventListener('chatwoot:closed', onClosed);
    return () => {
      window.removeEventListener('chatwoot:ready', onReady);
      window.removeEventListener('chatwoot:opened', onOpened);
      window.removeEventListener('chatwoot:closed', onClosed);
    };
  }, [setNavigation]);

  // SDK einmalig laden, sobald Consent-Lage geklärt ist
  useEffect(() => {
    if (!consentOk || !BASE_URL || !WEBSITE_TOKEN) return;
    if (window.$chatwoot || document.getElementById(SCRIPT_ID)) return;

    window.chatwootSettings = {
      hideMessageBubble: true, // Bubble erst nach KI-Handoff (showChat)
      position: 'right',
      locale: user?.locale === 'en' ? 'en' : 'de',
      type: 'standard',
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.defer = true;
    script.onload = () => window.chatwootSDK?.run({ websiteToken: WEBSITE_TOKEN, baseUrl: BASE_URL });
    document.body.appendChild(script);
  }, [consentOk, user?.locale]);

  // Eingeloggten User verifiziert identifizieren (HMAC vom Server)
  useEffect(() => {
    if (!ready || !user?.email || !user.chatwootIdentifierHash) return;
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    window.$chatwoot?.setUser(user.email, {
      email: user.email,
      name,
      identifier_hash: user.chatwootIdentifierHash,
    });
  }, [ready, user?.email, user?.chatwootIdentifierHash]);

  // Sichtbarkeit + Auto-Open beim Handoff aus dem KI-Chat
  useEffect(() => {
    if (!ready) return;
    window.$chatwoot?.toggleBubbleVisibility(showChat ? 'show' : 'hide');
    if (showChat) window.$chatwoot?.toggle('open');
  }, [ready, showChat]);

  return null;
}
